import PropTypes from 'prop-types'
import { X } from 'lucide-react'
import AiTextResult from './AiTextResult'
import AiTableResult from './AiTableResult'
import SmartVisualization from './SmartVisualization'
import DataCompletenessIndicator from './DataCompletenessIndicator'

/**
 * AiResultsDisplay Component
 *
 * Orchestrates the display of all query response sections:
 * - Text response with metadata
 * - Tables (rankings, facilities, data)
 * - Visualizations (optional, expandable)
 */
export default function AiResultsDisplay({ response, onClear }) {
  if (!response) return null

  const {
    type,
    text,
    data,
    rankings,
    facilities,
    resultCount,
    suggestions,
    insights,
    visualizations = [],
    cached,
    timestamp,
    tokensUsed,
    cacheKey,
    dataCompleteness,
    _metadata, // Metadata from cached queries
  } = response

  const hasVisualizations = visualizations && visualizations.length > 0
  const hasRankings = rankings && rankings.length > 0
  const hasFacilities = facilities && facilities.length > 0
  const hasData = data && Object.keys(data).length > 0
  const hasDataCompleteness =
    dataCompleteness && dataCompleteness.totalFacilities > 0

  return (
    <div className="space-y-4">
      {/* Main Response Card */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        {/* Clear Button - Top Right */}
        <div className="flex justify-end p-3 border-b border-gray-100">
          <button
            onClick={onClear}
            className="flex items-center gap-2 px-3 py-1.5 text-sm text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-4 h-4" />
            Clear Results
          </button>
        </div>

        {/* Text Result */}
        <AiTextResult
          type={type}
          text={text}
          cached={cached}
          timestamp={timestamp}
          tokensUsed={tokensUsed}
          cacheKey={cacheKey}
          metadata={_metadata}
          suggestions={suggestions}
          insights={insights}
        />

        {/* Tables Section */}
        {(hasRankings || hasFacilities || hasData) && (
          <div className="border-t border-gray-100">
            <AiTableResult
              type={type}
              data={data}
              rankings={rankings}
              facilities={facilities}
              resultCount={resultCount}
            />
          </div>
        )}

        {/* Metadata Footer */}
        {(timestamp || tokensUsed) && (
          <div className="px-4 py-3 bg-gray-50 border-t border-gray-100 flex flex-wrap items-center justify-between gap-2 text-xs text-gray-600">
            {timestamp && (
              <div>
                <span className="font-medium">Timestamp:</span>{' '}
                {new Date(timestamp).toLocaleString()}
              </div>
            )}
            {tokensUsed && (
              <div>
                <span className="font-medium">Tokens used:</span> {tokensUsed}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Data Completeness Indicator */}
      {hasDataCompleteness && (
        <DataCompletenessIndicator dataCompleteness={dataCompleteness} />
      )}

      {/* Visualizations Section - Separate Card */}
      {hasVisualizations && <SmartVisualization response={response} />}
    </div>
  )
}

AiResultsDisplay.propTypes = {
  response: PropTypes.shape({
    type: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    // Data can be either normalized (data_summary) or legacy format
    data: PropTypes.oneOfType([
      PropTypes.shape({
        type: PropTypes.oneOf(['data_summary']),
        format: PropTypes.oneOf(['simple', 'complex', 'raw']),
        sections: PropTypes.array,
        metadata: PropTypes.object,
      }),
      PropTypes.object, // Legacy format
    ]),
    rankings: PropTypes.array,
    facilities: PropTypes.array,
    resultCount: PropTypes.number,
    suggestions: PropTypes.array,
    insights: PropTypes.string,
    visualizations: PropTypes.array,
    cached: PropTypes.bool,
    timestamp: PropTypes.string,
    tokensUsed: PropTypes.number,
    cacheKey: PropTypes.string,
    dataCompleteness: PropTypes.object,
    dataSpec: PropTypes.object,
    _metadata: PropTypes.object,
  }).isRequired,
  onClear: PropTypes.func.isRequired,
}
